/**
 * Locale-aware number and date formatting (#799, docs/I18N.md "Formatting"). The existing display rules (cents with
 * up to 3 decimals, $ amounts with 2, grouping or not) stay exactly as they were: helpers build the English string
 * as before and then swap in the active locale's decimal/grouping separators and $/% placement. English output is
 * therefore byte-identical to the pre-i18n app; pt-BR shows "63,5¢" and "$ 1.234,50", ru "1 234,50 $" and "63,5 %".
 * Wire values stay '.'-decimal strings; never parse a localized string back into a number.
 */
import { getLocale, intlLocale, type LocaleCode } from './i18n';

interface Seps {
  group: string;
  decimal: string;
  usd: { prefix: string; suffix: string };
  pct: { prefix: string; suffix: string };
}

const sepCache = new Map<string, Seps>();

function affix(parts: Intl.NumberFormatPart[]): { prefix: string; suffix: string } {
  let prefix = '';
  let suffix = '';
  let seenNumber = false;
  for (const p of parts) {
    if (p.type === 'integer' || p.type === 'decimal' || p.type === 'fraction' || p.type === 'group') seenNumber = true;
    else if (p.type === 'minusSign' || p.type === 'plusSign') continue;
    // The sign is always "$" (owner decision #869); Arabic's narrow symbol would be "US$".
    else if (seenNumber) suffix += p.type === 'currency' ? '$' : p.value;
    else prefix += p.type === 'currency' ? '$' : p.value;
  }
  return { prefix, suffix };
}

function seps(code: LocaleCode = getLocale()): Seps {
  const tag = intlLocale(code);
  let s = sepCache.get(tag);
  if (!s) {
    const num = new Intl.NumberFormat(tag, { numberingSystem: 'latn' } as Intl.NumberFormatOptions).formatToParts(12345.6);
    const usd = new Intl.NumberFormat(tag, {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'narrowSymbol',
      numberingSystem: 'latn',
    } as Intl.NumberFormatOptions).formatToParts(1.5);
    const pct = new Intl.NumberFormat(tag, { style: 'percent', numberingSystem: 'latn' } as Intl.NumberFormatOptions).formatToParts(0.5);
    s = {
      group: num.find((p) => p.type === 'group')?.value ?? ',',
      decimal: num.find((p) => p.type === 'decimal')?.value ?? '.',
      usd: affix(usd),
      pct: affix(pct),
    };
    sepCache.set(tag, s);
  }
  return s;
}

/** True when the active locale formats exactly like the pre-i18n English app. */
function englishLike(s: Seps): boolean {
  return s.group === ',' && s.decimal === '.' && s.usd.prefix === '$' && !s.usd.suffix && s.pct.suffix === '%' && !s.pct.prefix;
}

/** An English-formatted number ("1,234.5", "-0.25") with the active locale's separators ("1.234,5" in pt-BR). */
export function localizeNumber(enFormatted: string): string {
  const s = seps();
  if (s.group === ',' && s.decimal === '.') return enFormatted;
  return enFormatted.replace(/[,.]/g, (c) => (c === ',' ? s.group : s.decimal));
}

/**
 * "$" + an English-formatted magnitude, with the locale's $ placement: en "$1,234.50", pt-BR "$ 1.234,50",
 * ru "1 234,50 $". `sign` ("-", "+" or "") goes in front, as in the English UI ("-$5.00").
 */
export function usd(enMagnitude: string, sign = ''): string {
  const s = seps();
  if (englishLike(s)) return `${sign}$${enMagnitude}`;
  return `${sign}${s.usd.prefix}${localizeNumber(enMagnitude)}${s.usd.suffix}`;
}

/**
 * Compact USD (#874 volume): "$" + an English-formatted mantissa + a unit text that is NOT localized again (it may
 * contain '.' or ',', e.g. a translated "тыс."): en "$1.2K", pt-BR "$ 1,2K", ru "1,2K $".
 */
export function usdCompact(enMantissa: string, unit: string): string {
  const s = seps();
  if (englishLike(s)) return `$${enMantissa}${unit}`;
  return `${s.usd.prefix}${localizeNumber(enMantissa)}${unit}${s.usd.suffix}`;
}

/** An English-formatted percentage number + the locale's % placement: en "63.5%", ru "63,5 %". */
export function percent(enNumber: string): string {
  const s = seps();
  if (englishLike(s)) return `${enNumber}%`;
  return `${s.pct.prefix}${localizeNumber(enNumber)}${s.pct.suffix}`;
}

/** Cents with the ¢ sign kept in every locale (owner default, docs/I18N.md). */
export function cents(enNumber: string): string {
  return `${localizeNumber(enNumber)}¢`;
}

/** Intl.NumberFormat for the active locale (Western digits always). */
export function numberFormat(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  return new Intl.NumberFormat(intlLocale(), { numberingSystem: 'latn', ...options } as Intl.NumberFormatOptions);
}

/**
 * Intl.DateTimeFormat for the active locale, on the locale's own calendar: Thai dates are converted to the Buddhist era
 * (2026 → 2569) by Intl, by owner decision (#869). Digits stay Western.
 */
export function dateTimeFormat(options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(intlLocale(), { numberingSystem: 'latn', ...options });
}

/**
 * Number inputs accept '.' and the locale's decimal separator ("0,55" in pt-BR/ru/vi): the typed value becomes a
 * '.'-decimal string before it reaches validation. A no-op in English.
 */
export function delocalizeInput(raw: string): string {
  const s = seps();
  return s.decimal === '.' ? raw : raw.split(s.decimal).join('.');
}
